import React, { useCallback, useMemo, useState, useEffect, useRef, useContext } from 'react';
import ReactFlow, {
    applyNodeChanges,
    applyEdgeChanges,
    Controls,
    Background,
    useReactFlow, 
    EdgeLabelRenderer,
    getBezierPath,
    Position,
    useStore,
    
} from 'reactflow';
import 'reactflow/dist/style.css'; 


import SituationNode from './SituationNode';
import ErrorOverlay from './ErrorOverlay';
import styles from './styles/Canvas.module.css'; 
import CanvasContext from '../../../context/CanvasContext';
import Edge, { getEllipsizedText } from './Edge'; 


import labelStyles from './styles/EdgeLabel.module.css';


const getHandleCoords = (pos, width, height, handlePos) => {
    if (!pos) return [undefined, undefined]; 
    let y = pos.y + height / 2;
    let x = pos.x + width / 2;
    switch(handlePos) {
        case Position.Left: x = pos.x; break;
        case Position.Right: x = pos.x + width; break;
        case Position.Top: y = pos.y; break;
        case Position.Bottom: y = pos.y + height; break;
        
    }
    if (isNaN(x) || isNaN(y)) {
        console.warn("getHandleCoords calculated NaN position:", { pos, width, height, handlePos, x, y });
        return [undefined, undefined];
    }
    return [x, y];
};


const EdgeLabelGroup = React.memo(({
    group,                      
    textMeasureCtx,             
    selectedEdgeId,             
    getChoice,                  
    labelStyles                 
}) => {
    

    
    const primaryEdge = useMemo(() => group[0], [group]);

    
    const sourceId = primaryEdge?.source;
    const targetId = primaryEdge?.target;

    
    const sourceNode = useStore(useCallback(s => {
        if (!sourceId) return undefined;
        return s.nodeInternals.get(sourceId);
    }, [sourceId]));

    const targetNode = useStore(useCallback(s => {
        if (!targetId) return undefined;
        return s.nodeInternals.get(targetId);
    }, [targetId]));

    
    const positionsAndDimensions = useMemo(() => {
        if (!sourceNode?.positionAbsolute || !targetNode?.positionAbsolute) return null;

        const sourceAbsPos = sourceNode.positionAbsolute;
        const targetAbsPos = targetNode.positionAbsolute;
        const sourceWidth  = sourceNode.width  || 150;
        const sourceHeight = sourceNode.height || 50;
        const targetWidth  = targetNode.width  || 150;
        const targetHeight = targetNode.height || 50;

        if (
            isNaN(sourceAbsPos.x) || isNaN(sourceAbsPos.y) ||
            isNaN(targetAbsPos.x) || isNaN(targetAbsPos.y) ||
            isNaN(sourceWidth)    || isNaN(sourceHeight) ||
            isNaN(targetWidth)    || isNaN(targetHeight)
        ) {
            return null;
        }

        return { sourceAbsPos, targetAbsPos, sourceWidth, sourceHeight, targetWidth, targetHeight };
    }, [sourceNode, targetNode]);

    
    const labelPosition = useMemo(() => {
        if (!positionsAndDimensions || !primaryEdge) return null;

        const {
            sourceAbsPos, targetAbsPos,
            sourceWidth,  sourceHeight,
            targetWidth,  targetHeight
        } = positionsAndDimensions;

        const isLoop = primaryEdge.source === primaryEdge.target;
        const sourcePosition = primaryEdge.sourcePosition || Position.Right;
        const targetPosition = primaryEdge.targetPosition || Position.Left;

        const [sourceEdgeX, sourceEdgeY] = getHandleCoords(sourceAbsPos, sourceWidth, sourceHeight, sourcePosition);
        const [targetEdgeX, targetEdgeY] = getHandleCoords(targetAbsPos, targetWidth, targetHeight, targetPosition);

        if (
            sourceEdgeX === undefined || sourceEdgeY === undefined ||
            targetEdgeX === undefined || targetEdgeY === undefined
        ) {
            return null;
        }

        let labelX, labelY;

        if (isLoop) {
            const radiusY = 50;
            labelX = sourceAbsPos.x + sourceWidth / 2;
            labelY = sourceAbsPos.y - radiusY;
        } else {
            try {
                const [ , x, y ] = getBezierPath({
                    sourceX: sourceEdgeX, sourceY: sourceEdgeY, sourcePosition,
                    targetX: targetEdgeX, targetY: targetEdgeY, targetPosition
                });
                labelX = x;
                labelY = y;
            } catch {
                return null;
            }
        }

        if (
            labelX === undefined || labelY === undefined ||
            isNaN(labelX)   || isNaN(labelY)
        ) {
            return null;
        }

        return { labelX, labelY };
    }, [
        positionsAndDimensions,
        primaryEdge?.id,
        primaryEdge?.sourcePosition,
        primaryEdge?.targetPosition
    ]);

    
    if (!labelPosition) return null;
    const { labelX, labelY } = labelPosition;

    
    const boxWidth           = 86;
    const boxHeight          = 20;
    const padding            = 4;
    const stackVerticalOffset = 25;

    
    if (group.length === 1) {
        const edgeItem = group[0];
        const isSingleSelected = selectedEdgeId === edgeItem.id;
        const maxTextWidth = boxWidth - padding * 2;
        const truncatedLabel = getEllipsizedText(edgeItem.data?.label || '', maxTextWidth, textMeasureCtx);

        return (
            <div
                key={`label-${edgeItem.id}`}
                style={{
                    position: 'absolute',
                    transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                    pointerEvents: 'all',
                    zIndex: 4,
                    cursor: 'pointer',
                }}
                className={`${labelStyles.edgeLabelWrapper} ${isSingleSelected ? labelStyles.selected : ''}`}
                onClick={(e) => { e.stopPropagation(); getChoice(edgeItem.id); }}
            >
                <div
                    className={labelStyles.edgeLabelBox}
                    style={{
                        width:  `${boxWidth}px`,
                        height: `${boxHeight}px`,
                        backgroundColor: isSingleSelected ? '#006666' : '#fff',
                        color: isSingleSelected ? '#fff' : '#000'
                    }}
                >
                    {truncatedLabel}
                </div>
            </div>
        );
    }

    
    const finalStackLabelY = labelY + stackVerticalOffset;
    return (
        <div
            key={`label-group-${primaryEdge.id}`}
            style={{
                position: 'absolute',
                transform: `translate(-50%, -100%) translate(${labelX}px, ${finalStackLabelY}px)`,
                pointerEvents: 'all',
                zIndex: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
            className={labelStyles.edgeLabelStackWrapper}
        >
            {group.map((edgeItem, idx) => {
                const isItemSelected       = selectedEdgeId === edgeItem.id;
                const isOppositeDirection  = edgeItem.source === primaryEdge.target && edgeItem.target === primaryEdge.source;
                let fill                   = '#fff';
                if (isOppositeDirection) fill = '#e6e6e6';
                if (isItemSelected)      fill = '#006666';
                const textColor           = isItemSelected ? '#fff' : '#000';
                const truncatedLabel      = getEllipsizedText(edgeItem.data?.label || '', boxWidth - padding * 2, textMeasureCtx);

                return (
                    <div
                        key={`label-${edgeItem.id}`}
                        className={`${labelStyles.edgeLabelWrapper} ${isItemSelected ? labelStyles.selected : ''} ${isOppositeDirection ? labelStyles.opposite : ''}`}
                        style={{
                            cursor: 'pointer',
                            width:  `${boxWidth}px`,
                            height: `${boxHeight}px`,
                            marginBottom: idx < group.length - 1 ? '-1px' : '0',
                            pointerEvents: 'all',
                        }}
                        onClick={(e) => { e.stopPropagation(); getChoice(edgeItem.id); }}
                    >
                        <div
                            className={labelStyles.edgeLabelBox}
                            style={{ backgroundColor: fill, color: textColor }}
                        >
                            {truncatedLabel}
                        </div>
                    </div>
                );
            })}
        </div>
    );
});





const Canvas = ({ setRenderEditSidebar, isMobile }) => {
    
    const {
        nodes, setNodes, edges, setEdges, scenario,
        selectedEdgeId, selectedNodeIds, onSelectNode, onSelectNodes, onSelectEdge,
        onCanvasClick, getSituation, getChoice, setGetZoomFunc,
        setProjectFunc, setReactFlowWrapper, edgeConnectingNodeId, setEdgeConnectingNodeId,
        createSituationAndNode,
        updateNodePosition,
        createChoiceAndEdge,
    } = useContext(CanvasContext);

    const { project, getZoom } = useReactFlow();
    const reactFlowWrapper = useRef(null);
    const lastTapRef = useRef(0);

    useEffect(() => {
        setGetZoomFunc(() => getZoom);
        setProjectFunc(() => project);
        setReactFlowWrapper(reactFlowWrapper);
    }, [getZoom, project, setGetZoomFunc, setProjectFunc, setReactFlowWrapper]);

    
    const onNodesChange = useCallback(
        (changes) => {
            

            
            let finalNodesState;
            setNodes((currentNodes) => {
                finalNodesState = applyNodeChanges(changes, currentNodes);
                return finalNodesState;
            });

            
            const dragStopChanges = changes.filter(change => {
                if (change.dragging === false) {
                    const wasNodeDraggedInBatch = changes.some(posChange =>
                        posChange.id === change.id && posChange.type === 'position'
                    );
                    return wasNodeDraggedInBatch;
                }
                return false;
            });

            if (dragStopChanges.length > 0) {
                

                dragStopChanges.forEach(stopChange => {
                    const changedNodeId = stopChange.id;
                    const nodeInFinalState = finalNodesState?.find(n => n.id === changedNodeId);

                    if (nodeInFinalState && nodeInFinalState.position) {
                        
                        
                        updateNodePosition(changedNodeId, nodeInFinalState.position);
                    } else {
                        console.warn(`Drag stopped for ${changedNodeId}, but couldn't retrieve final node or position.`);
                    }
                });
            }
        },
        
        [setNodes, updateNodePosition]
    );

    const onEdgesChange = useCallback(
        (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
        [setEdges]
    );

     const onConnect = useCallback(async (params) => {
        try {
            const { source, target } = params;
            
            const sourceNodeExists = nodes.some(n => n.id === source);
            const targetNodeExists = nodes.some(n => n.id === target);

            if (!sourceNodeExists || !targetNodeExists) {
                console.warn("Attempted to connect non-existent nodes:", params);
                return; 
            }

            const hasHint = (source === target);
            console.log("this newly created choice will have a hint!", hasHint);
            const newEdge = await createChoiceAndEdge(source, target, hasHint); 

            
            if (newEdge && newEdge.id) {
                 onSelectEdge(newEdge.id); 
            }


            if (!isMobile && typeof setRenderEditSidebar === 'function') {
                setRenderEditSidebar(true);
            } else if (typeof setRenderEditSidebar !== 'function') {
                 console.warn("setRenderEditSidebar is not a function");
            }

        } catch (error) {
            console.error(`onConnect failed: ${error}`);
        }
    }, [createChoiceAndEdge, isMobile, setRenderEditSidebar, nodes, onSelectEdge]); 


    const onEdgeClick = useCallback(async (event, edge) => {
        event.stopPropagation(); 
        await getChoice(edge.id);
        
        
        
        
    }, [getChoice]); 


    const onNodeClick = useCallback((event, node) => {
        event.stopPropagation(); 
        const { id } = node;
        const currentTime = new Date().getTime();
        const tapLength = currentTime - lastTapRef.current;
        const doubleTapThreshold = 300; 

        const alreadySelected = selectedNodeIds.includes(id);

        if (isMobile) {
            
            if (tapLength < doubleTapThreshold) {
                
                 event.preventDefault(); 
                 setEdgeConnectingNodeId(prevId => (prevId === id ? null : id)); 
                 
                 lastTapRef.current = 0; 
            } else {
                
                if (edgeConnectingNodeId && edgeConnectingNodeId !== id) {
                    
                    onConnect({ source: edgeConnectingNodeId, target: id });
                    setEdgeConnectingNodeId(null); 
                } else if (edgeConnectingNodeId && edgeConnectingNodeId === id) {
                    
                     setEdgeConnectingNodeId(null);
                     
                     
                     
                }
                 else {
                    
                    getSituation(id); 
                    if (alreadySelected) {
                         onCanvasClick(); 
                    } else {
                        onSelectNode(id); 
                    }
                }
                lastTapRef.current = currentTime; 
            }
        } else {
            
            const isMultiSelect = event.ctrlKey || event.metaKey;

            if (isMultiSelect) {
                const newSelectedNodeIds = alreadySelected
                    ? selectedNodeIds.filter(nId => nId !== id)
                    : [...selectedNodeIds, id];
                onSelectNodes(newSelectedNodeIds); 
                
                
            } else {
                
                getSituation(id); 
                 if (alreadySelected && selectedNodeIds.length === 1) {
                    
                     onCanvasClick();
                 } else {
                    
                    onSelectNode(id);
                 }
            }
        }
    }, [
        isMobile,
        edgeConnectingNodeId,
        setEdgeConnectingNodeId,
        onConnect,
        getSituation,
        onSelectNode,
        onSelectNodes,
        onCanvasClick,
        selectedNodeIds,
    ]); 


    const onDrop = useCallback(async (event) => {
        event.preventDefault();
        
        const wrapperBounds = reactFlowWrapper.current?.getBoundingClientRect(); 

        if (!wrapperBounds) {
            console.error("ReactFlow wrapper bounds not available.");
            return;
        }

        const nodeType = event.dataTransfer.getData('application/reactflow');
        if (typeof nodeType === 'undefined' || !nodeType) {
            return;
        }

        if (typeof project !== 'function' || typeof getZoom !== 'function') {
             console.error("project or getZoom function not available for onDrop");
             return;
        }

        try {
            
            const position = project({
                x: event.clientX - wrapperBounds.left,
                y: event.clientY - wrapperBounds.top,
            });

            const initialSituationData = {
                isLogged: scenario?.isLogged || false,
                text: '',
                isStart: nodeType === 'startNode',
                isEnd: nodeType === 'endNode',
            };

            
            const newNode = await createSituationAndNode(position, nodeType, initialSituationData);

            
            if (newNode && newNode.id) {
                onSelectNode(newNode.id); 
                if (typeof setRenderEditSidebar === 'function') {
                    setRenderEditSidebar(true);
                }
            }

        } catch (error) {
            console.error(`Error handling drop event: ${error}`);
        }
    }, [
        scenario,
        createSituationAndNode,
        setRenderEditSidebar,
        project,
        getZoom,
        onSelectNode, 
        
    ]); 


    
    const nodeTypes = useMemo(() => ({
        
        situation: (nodeProps) => {
            const isSelected = selectedNodeIds.includes(nodeProps.id);
            const isEdgeConnecting = edgeConnectingNodeId === nodeProps.id;
            return <SituationNode {...nodeProps} selected={isSelected} edgeConnecting={isEdgeConnecting} />;
        },
        
        
        
    }), [selectedNodeIds, edgeConnectingNodeId]); 

    const edgeTypes = useMemo(() => ({
        
        choice: Edge, 
        
        
    }), []); 


    
    const edgeGroups = useMemo(() => {
        const groups = new Map(); 
        edges.forEach(edge => {
            
            const sourceId = String(edge.source);
            const targetId = String(edge.target);
            const key = [sourceId, targetId].sort().join('-');

            if (!groups.has(key)) {
                groups.set(key, []);
            }
            
            const group = groups.get(key);
            if(group) { 
                 group.push(edge);
            }
        });

        
        groups.forEach(groupEdges => {
            groupEdges.sort((a, b) => String(a.id).localeCompare(String(b.id)));
        });

        return Array.from(groups.values()); 
    }, [edges]); 


    
    const textMeasureCtx = useMemo(() => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (context) {
            context.font = "12px Arial"; 
        }
        return context;
   }, []); 


    
    return (
        <div
            onDrop={onDrop}
            onDragOver={(event) => event.preventDefault()}
            className={styles.container}
            ref={reactFlowWrapper}
        >
            <ReactFlow
                nodes={nodes}
                onNodesChange={onNodesChange}
                edges={edges}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onEdgeClick={onEdgeClick}
                onPaneClick={onCanvasClick}
                onNodeClick={onNodeClick}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                fitView
                deleteKeyCode={['Delete', 'Backspace']}
                aria-label="Scenario flow canvas"
            >
                <Controls />
                <Background color="#aaa" gap={16} />

                {/* ---- Edge Label Renderer ---- */}
                <EdgeLabelRenderer>
                    {edgeGroups.map((group) => {
                        
                        if (group.length === 0) return null;

                        

                        
                        return (
                             <EdgeLabelGroup
                                key={`group-${group[0].id}`}
                                group={group}
                                textMeasureCtx={textMeasureCtx}
                                selectedEdgeId={selectedEdgeId}
                                getChoice={getChoice}
                                labelStyles={labelStyles}
                             />
                        );
                    })}
                </EdgeLabelRenderer>
                {/* -------------------------------- */}

            </ReactFlow>
            <ErrorOverlay
                onSelectNode={onSelectNode}
                onSelectEdge={onSelectEdge}
            />
        </div>
    );
};

export default Canvas;